// Service items — flat single-entity list. Each item carries its own sales GL and one
// price row per activated company currency (rate + unit + tax). No categories, no FX
// at pricing time. See plan/pricing-redesign.md on the API and the mockup at
// https://claude.ai/artifact/NHwe1Y6BjM2yvbVpsBugPL.
//
// products.js's wrapper mounts the { node, onShow } this returns.
import { html, signal } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { t } from '../shared/i18n.js';
import { icon } from '../shared/icons.js';
import { searchSelect } from '../shared/searchselect.js';
import { fmtRate } from '../shared/formats.js';
import { notice as toast } from '../shared/notice.js';
import { rowDelete } from '../shared/confirm.js';
import { pager } from '../shared/pager.js';


export function buildStockTab(companyId){
  const base = '/companies/' + companyId + '/inventory';

  const opts = signal(null);
  const items = signal([]);
  const q = signal('');
  const total = signal(0), page = signal(1), pages = signal(1), loading = signal(false);
  const listErr = signal('');
  const PER_PAGE = 20;
  let searchTimer = null, loadSeq = 0;
  const visibleItems = () => items.value;
  const itemCountLabel = () => {
    const count = total.value || 0;
    return count + ' ' + (count === 1 ? t('stock.item', 'item') : t('stock.items', 'items'));
  };

  async function loadOpts(){ opts.value = await api.get(base + '/item-options'); }
  async function loadItems(nextPage = page.value){
    const seq = ++loadSeq;
    loading.value = true; listErr.value = '';
    const params = new URLSearchParams({ page: String(nextPage), per_page: String(PER_PAGE) });
    if(q.value.trim()) params.set('q', q.value.trim());
    try {
      const r = await api.get(base + '/items?' + params);
      if(seq !== loadSeq) return;
      items.value = r.data || [];
      total.value = Number(r.total) || 0;
      page.value = Number(r.page) || 1;
      pages.value = Number(r.total_pages) || 1;
    } catch(e){
      if(seq !== loadSeq) return;
      items.value = []; total.value = 0; pages.value = 1;
      listErr.value = (e.data && e.data.error) || t('stock.load_failed', 'Could not load service items');
    } finally {
      if(seq === loadSeq) loading.value = false;
    }
  }
  function onSearch(e){
    q.value = e.target.value; delId.value = null; page.value = 1;
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => loadItems(1), 250);
  }
  function goPage(nextPage){
    page.value = nextPage;
    loadItems(nextPage);
  }

  // ── Item form ─────────────────────────────────────────────────────────────
  const formOpen = signal(false);
  const editing = signal(null);
  const busy = signal(false);
  const err = signal('');
  const delId = signal(null);
  const delBusy = signal(false);
  const delErr = signal('');
  const iCode = signal('');
  const iName = signal('');
  let iAcct = '';                    // handled by searchSelect widget; not reactive here
  //  priceRows is the LIVE array the form's inputs mutate in place. It is only ASSIGNED
  //  (which triggers a re-render) when the form opens on a fresh item; typing on any input
  //  updates the object in place so the input keeps focus across keystrokes.
  let priceRows = [];

  function seedPricesFrom(existing){
    const byCcy = new Map((existing || []).map(p => [Number(p.currency_id), p]));
    return (opts.value?.currencies || []).map(c => {
      const p = byCcy.get(c.id);
      return {
        currency_id: c.id, currency_code: c.code, currency_symbol: c.symbol, is_base: !!c.is_base,
        // Two decimals on load so a stored 100 renders as "100.00" — the user's own
        // typing is left as-is (Number() on save picks it up either way).
        rate: p ? Number(p.rate).toFixed(2) : '',
        unit: (p && p.unit) || 'hour',
        sales_tax_code_id: p ? p.sales_tax_code_id : null,
      };
    });
  }

  function openAdd(){
    editing.value = null;
    iCode.value = ''; iName.value = ''; iAcct = '';
    priceRows = seedPricesFrom([]);
    err.value = ''; formOpen.value = true;
  }
  async function openEdit(row){
    const full = await api.get(base + '/items/' + row.id);
    editing.value = full.id;
    iCode.value = full.code; iName.value = full.name;
    iAcct = full.sales_account_id ? String(full.sales_account_id) : '';
    priceRows = seedPricesFrom(full.prices);
    err.value = ''; formOpen.value = true;
  }
  function closeForm(){ formOpen.value = false; }

  async function save(e){
    e.preventDefault();
    if(!iCode.value.trim() || !iName.value.trim()){
      err.value = t('stock.code_name_required', 'Code and name are required'); return;
    }
    // Blank rate → not priced in that currency; skip on save. At least one must be priced.
    // Accept a comma decimal (a user pasting a locale-formatted number) by normalising here.
    const norm = v => Number(String(v).replace(/,/g, '.').replace(/\s+/g, ''));
    const priced = priceRows.filter(p => p.rate !== '' && p.rate != null);
    if(!priced.length){
      err.value = t('stock.at_least_one_price', 'Fill a rate for at least one currency'); return;
    }
    for(const p of priced){
      const n = norm(p.rate);
      if(Number.isNaN(n) || n < 0){
        err.value = t('stock.rate_positive', 'Rates must be zero or more'); return;
      }
    }
    busy.value = true; err.value = '';
    const body = {
      code: iCode.value.trim(),
      name: iName.value.trim(),
      sales_account_id: iAcct ? Number(iAcct) : null,
      prices: priced.map(p => ({
        currency_id: Number(p.currency_id),
        rate: Number(p.rate),
        unit: (p.unit || 'hour').trim() || 'hour',
        sales_tax_code_id: p.sales_tax_code_id ? Number(p.sales_tax_code_id) : null,
      })),
    };
    try {
      if(editing.value){ await api.put(base + '/items/' + editing.value, body); }
      else             { await api.post(base + '/items', body); }
      await loadItems(page.value);
      closeForm();
      toast(t('stock.item_saved', 'Service item saved'));
    } catch(e2){
      err.value = (e2.data && e2.data.error) || t('stock.save_failed', 'Could not save');
    }
    busy.value = false;
  }

  function armDelete(row){ delErr.value = ''; delId.value = row.id; }
  function cancelDelete(){ delId.value = null; delErr.value = ''; }
  async function del(row){
    delBusy.value = true; delErr.value = '';
    try {
      await api.post(base + '/items/' + row.id + '/delete', {});
      delId.value = null;
      await loadItems(page.value);
      toast(t('stock.item_deleted', 'Deleted'));
    } catch(e){
      delErr.value = (e.data && e.data.error) || t('stock.delete_failed', 'Could not delete');
    }
    delBusy.value = false;
  }

  // ── Renders ────────────────────────────────────────────────────────────────
  const money = v => (v == null || v === '') ? '—'
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const priceChip = p => html`<span class="pill pill-open inv-price-chip" title="${p.currency_code} ${money(p.rate)} / ${p.unit}${p.tax_code ? ' · ' + p.tax_code : ''}">
    ${p.currency_symbol || p.currency_code} ${money(p.rate)}<span class="inv-price-unit">/${p.unit}</span></span>`;

  const list = () => html`
    <div class="inv-list">
      ${loading.value
        ? html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`
        : listErr.value
        ? html`<div class="muted-block">${listErr.value}</div>`
        : visibleItems().length === 0
        ? html`<div class="muted inv-empty">${q.value.trim()
          ? t('stock.no_search_matches', 'No service items match that search.')
          : t('stock.no_service_items', 'No service items yet — add one above.')}</div>`
        : visibleItems().map(i => html`
          <div class="inv-row" role="button" tabindex="0"
               aria-label=${t('stock.open_item', 'Edit service item') + ': ' + i.name}
               @click=${() => openEdit(i)}
               @keydown=${e => { if(e.key === 'Enter' || e.key === ' '){ e.preventDefault(); openEdit(i); } }}>
            <div class="inv-row-main">
              <div class="inv-row-name">${i.name} <span class="muted inv-row-code">· ${i.code}</span></div>
              <div class="inv-row-sub muted">${i.sales_account_code ? i.sales_account_code + ' — ' + (i.sales_account_name || '') : t('stock.no_sales_account', 'No sales account set')}</div>
            </div>
            <div class="inv-row-prices">
              ${(i.prices || []).length === 0
                ? html`<span class="pill pill-void">${t('stock.no_price', 'No prices set')}</span>`
                : (i.prices || []).map(priceChip)}
            </div>
            <span class="inv-row-delete">${rowDelete({ armed: delId.value === i.id, busy: delBusy.value && delId.value === i.id,
              error: delId.value === i.id ? delErr.value : '', onArm: () => armDelete(i), onCancel: cancelDelete,
              onDelete: () => del(i) })}</span>
          </div>`)}
    </div>`;

  function formNode(){
    // Render once when the form opens; priceRows is mutated in place so the inputs keep focus.
    const rows = priceRows;
    const frag = html`
      <form class="inv-form" @submit=${save}>
        <div class="inv-form-head">
          <div>
            <h3 class="inv-form-title">${editing.value ? t('stock.edit_item', 'Edit service item') : t('stock.add_item', 'Add Service Item')}</h3>
            <div class="muted inv-form-intro">${t('stock.item_intro', 'One row per activated currency — leave blank to skip that currency.')}</div>
          </div>
          <button type="button" class="btn ghost inv-form-close" @click=${closeForm}>${t('common.close', 'Close')}</button>
        </div>

        <label class="fld inv-code-field">
          <span>${t('stock.item_code', 'Code')} *</span>
          <input class="form-control inv-code-input" autocomplete="off" .value=${iCode}
                 @input=${e => iCode.value = e.target.value} ?disabled=${!!editing.value}>
        </label>
        <label class="fld">
          <span>${t('stock.item_name', 'Description')} *</span>
          <textarea class="form-control" rows="4" .value=${iName}
                    @input=${e => iName.value = e.target.value}
                    placeholder=${t('stock.eg_workshop', 'e.g. Strategy workshop — half-day facilitated session with the leadership team, delivered on-site.')}></textarea>
        </label>

        <label class="fld"><span>${t('stock.sales_account', 'Sales account')}</span><div data-item-acct></div>
          <div class="muted inv-form-hint">${t('stock.sales_account_hint', 'Every line of this item posts to this GL — leave blank to use the company default income account.')}</div></label>

        <div class=${'inv-prices' + (opts.value?.vat_registered ? '' : ' no-tax')}>
          <div class="inv-prices-head">
            <div>${t('stock.prices', 'Prices')}</div>
            <div class="muted inv-prices-hint">${t('stock.prices_hint', 'Blank rate = not priced in that currency')}</div>
          </div>

          <div class="inv-price-header">
            <div>${t('stock.currency', 'Currency')}</div>
            <div class="inv-price-header-rate">${t('stock.rate', 'Rate')}</div>
            <div>${t('stock.unit', 'Per')}</div>
            ${opts.value?.vat_registered ? html`<div>${opts.value?.tax_label || 'VAT'}</div>` : ''}
          </div>

          ${rows.length === 0
            ? html`<div class="muted inv-no-currencies">${t('stock.no_currencies_enabled', 'No currencies enabled — enable some in Company · Currencies first.')}</div>`
            : rows.map((p, idx) => html`
              <div class="inv-price-row">
                <div class="inv-price-ccy"><b>${p.currency_symbol || ''} ${p.currency_code}</b>${p.is_base ? html`<span class="inv-price-tag inherit">${t('stock.base', 'base')}</span>` : ''}</div>
                <input class="form-control text-right" type="text" inputmode="decimal" .value=${p.rate}
                       @input=${e => { rows[idx].rate = e.target.value; }}
                       placeholder=${t('stock.blank_not_priced', '— not priced —')} aria-label=${t('stock.rate', 'Rate')}>
                <input class="form-control" .value=${p.unit || 'hour'}
                       @input=${e => { rows[idx].unit = e.target.value; }}
                       placeholder="hour" aria-label=${t('stock.unit', 'Per')}>
                ${opts.value?.vat_registered ? html`<div data-item-tax></div>` : ''}
              </div>`)}
        </div>

        ${() => err.value ? html`<div class="row-err">${err.value}</div>` : ''}
        <div class="form-actions">
          <button type="button" class="btn" @click=${closeForm}>${t('common.cancel', 'Cancel')}</button>
          <button type="submit" class="btn primary" ?disabled=${busy.value}>${() => busy.value ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </div>
      </form>`;
    // html`` returns a fragment which is emptied when the reactive renderer mounts it. Keep the
    // actual form element so the imperative search selectors are mounted into the live DOM instead
    // of being appended to an already-empty fragment.
    const formEl = frag.querySelector('.inv-form');
    queueMicrotask(() => {
      const none = { value: '', label: t('stock.no_default', '— None —') };
      const accts = [none, ...((opts.value?.sales_accounts || []).map(a => ({
        value: String(a.id), label: a.code + ' — ' + a.name, keywords: a.name })))];
      const cell = formEl?.querySelector('[data-item-acct]');
      if(cell) cell.append(searchSelect({ options: accts, value: iAcct,
        ariaLabel: t('stock.sales_account', 'Sales account'),
        placeholder: t('stock.eg_sales_acct', 'e.g. 0400 — Consulting revenue'),
        onSelect: v => { iAcct = v; } }).el);

      // All meaningful option lists use the shared searchable lookup component. Keep the API's
      // configured tax-code order in the option list; searchSelect filters without re-sorting it.
      const taxOpts = [{ value: '', label: t('stock.no_tax_row', '— None —') },
        ...((opts.value?.tax_codes || []).map(tc => ({
          value: String(tc.id),
          label: tc.code + (tc.name ? ' · ' + tc.name : '') + ' · ' + fmtRate(tc.rate) + '%',
          keywords: [tc.code, tc.name, tc.rate].filter(Boolean).join(' '),
          sub: fmtRate(tc.rate) + '%'
        })) )];
      formEl?.querySelectorAll('[data-item-tax]').forEach((taxCell, idx) => {
        taxCell.append(searchSelect({
          options: taxOpts,
          value: rows[idx].sales_tax_code_id ? String(rows[idx].sales_tax_code_id) : '',
          className: 'form-control inv-tax-search',
          ariaLabel: t('stock.tax_code', 'VAT / tax code'),
          placeholder: t('stock.search_tax', 'Search VAT code…'),
          onSelect: v => { rows[idx].sales_tax_code_id = v ? Number(v) : null; }
        }).el);
      });
      // Every add/edit form starts where the user needs to type. On an edit the code is disabled,
      // so this naturally lands on the description; on an add it lands on the code.
      formEl?.querySelector('input:not([disabled]), textarea, select, .ss-input')?.focus();
    });
    return frag;
  }

  const node = html`
    <div class="inv-page">
      <div class="ux-list-actions">
        <input class="search ux-search inv-search" type="search" .value=${q}
          @input=${onSearch}
          placeholder=${t('stock.search_placeholder', 'Search by code or description…')}
          aria-label=${t('stock.search_items', 'Search service items')}>
        <button class="btn primary" @click=${openAdd}>${icon('plus', {size:14})} ${t('stock.add_item', 'Add Service Item')}</button>
      </div>
      <div class="inv-toolbar">
        <div class="muted inv-count list-count">${itemCountLabel}</div>
      </div>
      ${() => formOpen.value ? formNode() : list()}
      ${() => pager({ page: page.value, totalPages: pages.value, total: total.value,
        perPage: PER_PAGE, onPage: goPage })}
    </div>`;

  async function onShow(){
    await loadOpts();
    await loadItems();
  }

  return { node, onShow };
}
